/**
 * The TikTok creator profile shown in Present View. Pure — unit-tested in
 * creator.test.js.
 *
 * The API reports the video's author when it can find it (X-Creator-*
 * headers). Otherwise TokDraw takes the @handle from full video links and the
 * name and avatar from the creator's own comment, and the host can fill in or
 * upload the rest.
 */
import { userKey } from './entries.js';

// Same handle as the backend's mock provider (DEMO_CREATOR in mock.provider.ts).
const DEMO_USERNAME = 'tokdraw.demo';

const SAFE_AVATAR = /^(?:https:\/\/|data:image\/(?:png|jpeg|webp);base64,)/i;
const MAX_AVATAR_LENGTH = 400_000; // an uploaded avatar is a ~256 px data URL

export const emptyCreator = () => ({ username: '', displayName: '', avatar: '' });

/** "@Shop.MM " → "Shop.MM" */
export function normalizeHandle(text) {
  return String(text ?? '')
    .normalize('NFC')
    .replace(/\s+/g, '')
    .replace(/^@+/, '')
    .slice(0, 64);
}

/** The @handle in a full video link, or '' (short links and raw IDs carry none). */
export function handleFromUrl(input) {
  const match = /tiktok\.com\/@([\w.]{2,64})(?=[/?#]|$)/i.exec(String(input ?? ''));
  return match ? match[1] : '';
}

/** Coerces saved/edited data into a creator: strings only, avatar https: or an uploaded image. */
export function sanitizeCreator(value) {
  const source = value && typeof value === 'object' ? value : {};
  const text = (v, max) => (typeof v === 'string' ? v.normalize('NFC').replace(/\s+/g, ' ').trim().slice(0, max) : '');
  const avatar = typeof source.avatar === 'string' ? source.avatar.trim() : '';
  return {
    username: typeof source.username === 'string' ? normalizeHandle(source.username) : '',
    displayName: text(source.displayName, 80),
    avatar: avatar.length <= MAX_AVATAR_LENGTH && SAFE_AVATAR.test(avatar) ? avatar : '',
  };
}

/** The headline over Present View and the recap video. */
export function creatorTitle(creator) {
  if (creator.username) return `Giveaway by @${creator.username}`;
  if (creator.displayName) return `Giveaway by ${creator.displayName}`;
  return 'TikTok giveaway';
}

/** The profile name, when it adds something beyond the handle. */
export function creatorSubtitle(creator) {
  return creator.username && userKey(creator.displayName) !== userKey(creator.username) ? creator.displayName : '';
}

export const isDemoCreator =(creator) => userKey(creator?.username) === DEMO_USERNAME;

/** Fills a missing name/avatar from a comment the creator left on their own video. */
export function fillFromComments(creator, comments) {
  if (!creator.username || (creator.displayName && creator.avatar)) return creator;
  const key = userKey(creator.username);
  const own = (comments ?? []).find((c) => userKey(c.username) === key);
  if (!own) return creator;
  return {
    ...creator,
    displayName: creator.displayName || String(own.displayName ?? '').trim(),
    avatar: creator.avatar || (/^https:\/\//i.test(own.avatar ?? '') ? own.avatar : ''),
  };
}

/**
 * The creator after loading comments. The server's answer (`fetched`) wins,
 * then a handle in the link. A different handle means a different creator, so
 * the old name and avatar are dropped. For the same creator, a typed name and
 * an uploaded avatar are kept, but a fresh TikTok avatar replaces a saved one
 * (TikTok's signed CDN links expire).
 */
export function resolveCreator(current, { url = '', comments = [], fetched = null, demo = false } = {}) {
  let creator = sanitizeCreator(current);
  if (!demo && isDemoCreator(creator)) creator = emptyCreator();

  // Trying demo data never replaces a profile the host already has.
  const found = demo && creator.username ? emptyCreator() : sanitizeCreator(fetched);
  const handle = found.username || handleFromUrl(url);
  if (handle && userKey(handle) !== userKey(creator.username)) creator = { ...emptyCreator(), username: handle };
  if (found.username) {
    creator = {
      ...creator,
      displayName: creator.displayName || found.displayName,
      avatar: creator.avatar.startsWith('data:') ? creator.avatar : found.avatar || creator.avatar,
    };
  }
  return fillFromComments(creator, comments);
}
